import React from 'react';
import { Card, Row, Col, Descriptions, Tag, Space, Typography, Divider, Button, Alert } from 'antd';
import { 
  CustomerServiceOutlined,
  MailOutlined, 
  PhoneOutlined, 
  ClockCircleOutlined,
  CodeOutlined,
  CalendarOutlined,
  SafetyCertificateOutlined,
  InfoCircleOutlined,
  TeamOutlined,
  BugOutlined,
  QuestionCircleOutlined,
  ToolOutlined
} from '@ant-design/icons';

const { Title, Text, Paragraph } = Typography;

const SupportInfo = () => {
  const supportData = {
    software: {
      name: "LifestyleERP",
      version: "6.0.0",
      description: "An ERP solution to manage business operations including sales, purchase, inventory, HR, and finance.",
      releaseDate: "2023-06-16",
      license: "MIT",
      lastUpdate: "2025-09-21"
    },
    supportTeam: {
      teamName: "LifestyleERP Support Team",
      teamSize: "5 Members",
      availability: "24/7 Support Available",
      responseTime: "Within 2-4 hours",
      supportTypes: ["Technical Support", "Bug Reports", "Feature Requests", "Training", "Maintenance"]
    },
    contact: {
      email: "info@lifestyleerp.com",
      phone: "+8801521430255",
      emergencyPhone: "+8801686076067",
      // supportPortal: "https://support.lifestyleerp.com",
      // documentation: "https://docs.lifestyleerp.com"
    },
    technicalDetails: {
      technologies: ["React", "Redux", "Django", "Django Rest Framework", "PostgreSQL", "TailwindCSS"],
      serverStatus: "Online",
      lastMaintenance: "2025-09-15",
      nextMaintenance: "2025-10-15"
    }
  };

  const handleContactSupport = (type, value) => {
    switch(type) {
      case 'email':
        window.open(`mailto:${value}?subject=LifestyleERP Support Request`);
        break;
      case 'phone':
      case 'emergency':
        window.open(`tel:${value}`);
        break;
      case 'portal':
      case 'docs':
        window.open(value, '_blank');
        break;
      default:
        break;
    }
  };

  return (
    <div style={{ padding: '24px', background: '#f5f5f5' }}>
      {/* Header Section */}
      <Card 
        style={{ 
          marginBottom: '24px',
          background: 'linear-gradient(135deg, #5e0101ff 0%, #8f1111ff 100%)',
          border: 'none',
          color: 'white'
        }}
        bodyStyle={{ padding: '32px' }}
      >
        <Row align="middle" gutter={[24, 24]}>
          <Col>
            <CustomerServiceOutlined 
              style={{ 
                fontSize: '64px',
                color: 'rgba(255,255,255,0.9)',
                backgroundColor: 'rgba(255,255,255,0.1)',
                padding: '16px',
                borderRadius: '50%'
              }} 
            />
          </Col>
          <Col flex={1}>
            <Title level={2} style={{ color: 'white', margin: 0 }}>
              {supportData.supportTeam.teamName}
            </Title>
            <Title level={4} style={{ color: 'rgba(255,255,255,0.8)', margin: '8px 0' }}>
              {supportData.supportTeam.availability}
            </Title>
            <Text style={{ color: 'rgba(255,255,255,0.9)', fontSize: '16px' }}>
              Get help with {supportData.software.name} - {supportData.supportTeam.teamSize} ready to assist
            </Text>
          </Col>
        </Row>
      </Card>

      <Row gutter={[24, 24]}>
        {/* Software Information */}
        <Col xs={24} lg={12}>
          <Card
            title={
              <Space>
                <InfoCircleOutlined style={{ color: '#1890ff' }} />
                <span>Software Information</span>
              </Space>
            }
            style={{ height: '100%' }}
            headStyle={{ backgroundColor: '#f8f9fa', borderBottom: '2px solid #e9ecef' }}
          >
            <Descriptions column={1} bordered size="small">
              <Descriptions.Item 
                label={<Text strong>Application Name</Text>}
              >
                <Tag color="blue" style={{ fontSize: '14px', padding: '4px 8px' }}>
                  {supportData.software.name}
                </Tag>
              </Descriptions.Item>
              
              <Descriptions.Item 
                label={<Text strong>Current Version</Text>}
              >
                <Tag color="green">{supportData.software.version}</Tag>
              </Descriptions.Item>
              
              <Descriptions.Item 
                label={<Text strong>Release Date</Text>}
              >
                <Space>
                  <CalendarOutlined />
                  <Text>{supportData.software.releaseDate}</Text>
                </Space>
              </Descriptions.Item>
              
              <Descriptions.Item 
                label={<Text strong>Last Update</Text>}
              >
                <Space>
                  <ClockCircleOutlined />
                  <Text>{supportData.software.lastUpdate}</Text>
                </Space>
              </Descriptions.Item>

              <Descriptions.Item 
                label={<Text strong>License Type</Text>}
              >
                <Space>
                  <SafetyCertificateOutlined />
                  <Tag color="orange">{supportData.software.license}</Tag>
                </Space>
              </Descriptions.Item>

              <Descriptions.Item 
                label={<Text strong>Server Status</Text>}
              >
                <Tag color="green" icon={<div style={{ width: '8px', height: '8px', backgroundColor: '#52c41a', borderRadius: '50%', display: 'inline-block', marginRight: '6px' }}></div>}>
                  {supportData.technicalDetails.serverStatus}
                </Tag>
              </Descriptions.Item>
            </Descriptions>
            
            <Divider />
            
            <div>
              <Text strong style={{ display: 'block', marginBottom: '8px' }}>
                <InfoCircleOutlined /> About the Software:
              </Text>
              <Paragraph style={{ marginBottom: 0, lineHeight: '1.6' }}>
                {supportData.software.description}
              </Paragraph>
            </div>
          </Card>
        </Col>

        {/* Support Services */}
        <Col xs={24} lg={12}>
          <Card
            title={
              <Space>
                <TeamOutlined style={{ color: '#52c41a' }} />
                <span>Support Services</span>
              </Space>
            }
            style={{ height: '100%' }}
            headStyle={{ backgroundColor: '#f8f9fa', borderBottom: '2px solid #e9ecef' }}
          >
            <Descriptions column={1} bordered size="small">
              <Descriptions.Item 
                label={<Text strong>Team Size</Text>}
              >
                <Tag color="blue">{supportData.supportTeam.teamSize}</Tag>
              </Descriptions.Item>
              
              <Descriptions.Item 
                label={<Text strong>Response Time</Text>}
              >
                <Space>
                  <ClockCircleOutlined />
                  <Text>{supportData.supportTeam.responseTime}</Text>
                </Space>
              </Descriptions.Item>
              
              <Descriptions.Item 
                label={<Text strong>Availability</Text>}
              >
                <Tag color="green">{supportData.supportTeam.availability}</Tag>
              </Descriptions.Item>
            </Descriptions>

            <Divider />

            <div>
              <Text strong style={{ display: 'block', marginBottom: '12px' }}>
                <ToolOutlined /> We Help With:
              </Text>
              <Space wrap>
                {supportData.supportTeam.supportTypes.map((service, index) => (
                  <Tag
                    key={service}
                    color={['blue', 'red', 'purple', 'orange', 'cyan'][index] || 'default'}
                    style={{
                      fontSize: '12px',
                      padding: '4px 8px',
                      borderRadius: '12px',
                      margin: '2px'
                    }}
                  >
                    {service}
                  </Tag>
                ))}
              </Space>
            </div>

            <Divider />

            {/* <div>
              <Text strong style={{ display: 'block', marginBottom: '8px' }}>
                <CodeOutlined /> Technology Stack:
              </Text>
              <Space wrap size={[4, 4]}>
                {supportData.technicalDetails.technologies.map((tech) => (
                  <Tag key={tech} style={{ fontSize: '11px', margin: '2px' }}>
                    {tech}
                  </Tag>
                ))}
              </Space>
            </div> */}
          </Card>
        </Col>

        {/* Contact Support */}
        {/* <Col xs={24}> */}
          {/* <Card
            title={
              <Space>
                <CustomerServiceOutlined style={{ color: '#722ed1' }} />
                <span>Get Support</span>
              </Space>
            }
            headStyle={{ backgroundColor: '#f8f9fa', borderBottom: '2px solid #e9ecef' }}
          > */}
            {/* <Row gutter={[24, 16]}>
              <Col xs={24} sm={12} md={6}>
                <Card 
                  size="small" 
                  style={{ textAlign: 'center', cursor: 'pointer', height: '120px' }}
                  hoverable
                  onClick={() => handleContactSupport('email', supportData.contact.email)}
                >
                  <Space direction="vertical" size={8}>
                    <MailOutlined style={{ fontSize: '24px', color: '#1890ff' }} />
                    <Text strong>Email Support</Text>
                    <Text type="secondary" style={{ fontSize: '11px' }}>
                      General inquiries & tickets
                    </Text>
                  </Space>
                </Card>
              </Col>

              <Col xs={24} sm={12} md={6}>
                <Card 
                  size="small" 
                  style={{ textAlign: 'center', cursor: 'pointer', height: '120px' }}
                  hoverable
                  onClick={() => handleContactSupport('phone', supportData.contact.phone)}
                >
                  <Space direction="vertical" size={8}>
                    <PhoneOutlined style={{ fontSize: '24px', color: '#52c41a' }} />
                    <Text strong>Phone Support</Text>
                    <Text type="secondary" style={{ fontSize: '11px' }}>
                      Business hours support
                    </Text>
                  </Space>
                </Card>
              </Col>

              <Col xs={24} sm={12} md={6}>
                <Card 
                  size="small" 
                  style={{ textAlign: 'center', cursor: 'pointer', height: '120px', borderColor: '#ff4d4f' }}
                  hoverable
                  onClick={() => handleContactSupport('emergency', supportData.contact.emergencyPhone)}
                >
                  <Space direction="vertical" size={8}>
                    <BugOutlined style={{ fontSize: '24px', color: '#ff4d4f' }} />
                    <Text strong style={{ color: '#ff4d4f' }}>Emergency</Text>
                    <Text type="secondary" style={{ fontSize: '11px' }}>
                      Critical system issues
                    </Text>
                  </Space>
                </Card>
              </Col>

              <Col xs={24} sm={12} md={6}>
                <Card 
                  size="small" 
                  style={{ textAlign: 'center', cursor: 'pointer', height: '120px' }}
                  hoverable
                  onClick={() => handleContactSupport('portal', supportData.contact.supportPortal)}
                >
                  <Space direction="vertical" size={8}>
                    <QuestionCircleOutlined style={{ fontSize: '24px', color: '#fa8c16' }} />
                    <Text strong>Support Portal</Text>
                    <Text type="secondary" style={{ fontSize: '11px' }}>
                      Tickets & knowledge base
                    </Text>
                  </Space>
                </Card>
              </Col>
            </Row> */}

            {/* <Divider /> */}

            {/* Quick Contact Info */}
            {/* <Row gutter={[16, 8]} style={{ marginBottom: '16px' }}>
              <Col xs={24} sm={12}>
                <Text strong>📧 Email: </Text>
                <Text copyable>{supportData.contact.email}</Text>
              </Col>
              <Col xs={24} sm={12}>
                <Text strong>📞 Phone: </Text>
                <Text copyable>{supportData.contact.phone}</Text>
              </Col>
              <Col xs={24} sm={12}>
                <Text strong>🚨 Emergency: </Text>
                <Text copyable style={{ color: '#ff4d4f' }}>{supportData.contact.emergencyPhone}</Text>
              </Col>
              <Col xs={24} sm={12}>
                <Text strong>📚 Docs: </Text>
                <Button 
                  type="link" 
                  size="small" 
                  onClick={() => handleContactSupport('docs', supportData.contact.documentation)}
                  style={{ padding: 0, height: 'auto' }}
                >
                  View Documentation
                </Button>
              </Col>
            </Row> */}

            {/* Action Buttons */}
            {/* <Row justify="center" gutter={16}>
              <Col>
                <Button 
                  type="primary" 
                  icon={<MailOutlined />}
                  size="large"
                  onClick={() => handleContactSupport('email', supportData.contact.email)}
                >
                  Create Support Ticket
                </Button>
              </Col>
              <Col>
                <Button 
                  icon={<QuestionCircleOutlined />}
                  size="large"
                  onClick={() => handleContactSupport('portal', supportData.contact.supportPortal)}
                >
                  Visit Help Center
                </Button>
              </Col>
            </Row> */}
          {/* </Card> */}
        {/* </Col> */}
      </Row>
      {/* Status Alert */}
      <Alert
        message="System Status: All Services Online"
        description={`Last updated: ${supportData.technicalDetails.lastMaintenance} | Next maintenance: ${supportData.technicalDetails.nextMaintenance}`}
        type="success"
        showIcon
        style={{ marginBottom: '24px' }}
      />
      {/* Footer */}
      <Card style={{ marginTop: '24px', textAlign: 'center', backgroundColor: '#fafafa' }}>
        <Text type="secondary">
          <CustomerServiceOutlined /> {supportData.software.name} Support Team - Here to help you succeed
        </Text>
        <br />
        <Text type="secondary" style={{ fontSize: '12px' }}>
          Average response time: {supportData.supportTeam.responseTime} | Available {supportData.supportTeam.availability}
        </Text>
      </Card>
    </div>
  );
};

export default SupportInfo;